import React, { useState, useEffect, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, NavLink, useLocation } from 'react-router-dom'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Card from 'react-bootstrap/Card'
import Alert from 'react-bootstrap/Alert'
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'
import { useTranslation } from 'react-i18next'
import DropdownComponent from '../../../components/dropdown'
import { logout } from '../../../actions/userActions'
import { ToastContext } from '../../../App'
import ProfileDropdown from '../../../components/ProfileDropdown'
import MerchantSidebarMenu from '../../../components/MerchantSidebarMenu'
import backendAPI from '../../../utils/backendAPI'
import { businessRowToProfileFields } from '../../../utils/merchantContext'
import '../../../css/merchantProfile.css'
import '../../../css/HomePage.css'


function MerchantProfile() {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const location = useLocation()
	const showToast = useContext(ToastContext)
	const userLogin = useSelector((state) => state.userLogin)
	const { userInfo } = userLogin
	const { t, i18n } = useTranslation()
	//const [merchanID,setMerchanID]= useState({ dab: localStorage.getItem('merchant_id')});
	const merchanID= localStorage.getItem('merchant_id');

	const logoutHandler = () => {
		showToast?.(t('profile.loggedOut'), 'info')
		dispatch(logout())
		navigate('/login', { replace: true })
	}

	// Business Info State
	const [businessData, setBusinessData] = useState({
		business_name: '',
		address: '',
		category: '',
		phone: '',
		google_place_id: '',
		free_visit_for_review: false,
	})

	// Original data backup for cancel functionality
	const [originalBusinessData, setOriginalBusinessData] = useState({
		business_name: '',
		address: '',
		category: '',
		phone: '',
		google_place_id: '',
		free_visit_for_review: false,
	})



	// Language state
	const languageOptions = [
		{ value: 'en', label: 'nav.english' },
		{ value: 'fr', label: 'nav.french' },
	]

	const [selectedLang, setSelectedLang] = useState(languageOptions[0])

	useEffect(() => {
		const savedLang = localStorage.getItem('lang')
		if (savedLang) {
			const found = languageOptions.find((opt) => opt.value === savedLang)
			if (found) {
				setSelectedLang(found)
				i18n.changeLanguage(savedLang)
			}
		}
	}, [])

	const handleLanguageChange = (langValue) => {
		i18n.changeLanguage(langValue)
		const found = languageOptions.find((opt) => opt.value === langValue)
		setSelectedLang(found)
		localStorage.setItem('lang', langValue)
	}

	// UI State
	const [isEditMode, setIsEditMode] = useState(false)
	const [loading, setLoading] = useState(true)
	const [deleteLoading, setDeleteLoading] = useState(false)
	const [saveLoading, setSaveLoading] = useState(false)
	const [activeTab, setActiveTab] = useState(location.state?.tab || 'profile')
	const [error, setError] = useState(null)
	const [success, setSuccess] = useState(null)

	// Category and Reward Type Options
	const categoryOptions = [
		{ value: 'Café', label: 'Café' },
		{ value: 'Restaurant', label: 'Restaurant' },
		{ value: 'Beauty', label: 'Beauty' },
		{ value: 'Bubble Tea', label: 'Bubble Tea' },
		{ value: 'Grocery', label: 'Grocery' },
		{ value: 'Other', label: 'Other' },
	]



	// Fetch merchant profile data
	useEffect(() => {
		const fetchMerchantProfile = async () => {
			setLoading(true)
			setError(null)

			try {
				// Fetch business data
				const businessResponse = await backendAPI.get('/api/user-businesses/')

				if (businessResponse.data && businessResponse.data.length > 0) {
					const businessInfo = businessRowToProfileFields(businessResponse.data[0])
					if (businessInfo) {
						setBusinessData(businessInfo)
						setOriginalBusinessData(businessInfo)
					}
				} else {
					setError(t('profile.noBusinessFound'))
				}
			} catch (error) {
				console.error('Error fetching merchant profile:', error)
				setError(t('profile.failedToLoad'))
			} finally {
				setLoading(false)
			}
		}

		fetchMerchantProfile()
	}, [userInfo])

	// Handle Business Info changes
	const handleBusinessChange = (e) => {
		const { name, value, type, checked } = e.target
		setBusinessData((prev) => {
			const next = {
				...prev,
				[name]: type === 'checkbox' ? checked : value,
			}
			if (name === 'google_place_id' && !String(value).trim()) {
				next.free_visit_for_review = false
			}
			return next
		})
	}



	// Save changes
	const handleSave = async () => {
		const nextName = String(businessData.business_name || '').trim()
		const nextAddress = String(businessData.address || '').trim()
		const nextCategory = String(businessData.category || '').trim()
		const nextPhone = String(businessData.phone || '').trim()
		const nextGooglePlaceId = String(businessData.google_place_id || '').trim()
		const nextFreeVisitForReview = nextGooglePlaceId ? Boolean(businessData.free_visit_for_review) : false

		if (!nextName || !nextAddress || !nextCategory || !nextPhone) {
			setError(t('profile.requiredFields'))
			return
		}

		setError(null)
		setSuccess(null)
		setSaveLoading(true)

		try {
			await backendAPI.post('/api/business/create/', {
				businessName: nextName,
				address: nextAddress,
				category: nextCategory,
				phone: nextPhone,
				googlePlaceId: nextGooglePlaceId,
				freeVisitForReview: nextFreeVisitForReview,
			})

			const refreshed = {
				business_name: nextName,
				address: nextAddress,
				category: nextCategory,
				phone: nextPhone,
				google_place_id: nextGooglePlaceId,
				free_visit_for_review: nextFreeVisitForReview,
			}
			businessRowToProfileFields({
				business_name: nextName,
				address: nextAddress,
				category: nextCategory,
				phone: nextPhone,
				google_place_id: nextGooglePlaceId,
				free_visit_for_review: nextFreeVisitForReview,
				merchant_id: localStorage.getItem('merchant_id'),
			})
			setBusinessData(refreshed)
			setSuccess('Profile updated successfully!')
			setOriginalBusinessData(refreshed)
			setIsEditMode(false)
			setTimeout(() => setSuccess(null), 3000)
		} catch (error) {
			console.error('Error saving profile:', error)
			setError(error.response?.data?.error || t('profile.failedToSave'))
		} finally {
			setSaveLoading(false)
		}
	}

	
	// Cancel editing
	const handleCancel = () => {
		// Restore original data
		setBusinessData(originalBusinessData)
		setIsEditMode(false)
		setError(null)
	}

	// Delete account
	const handleDeleteAccount = async () => {
		const confirmed = window.confirm(t('profile.deleteConfirm'))

		if (!confirmed) return

		setDeleteLoading(true)
		setError(null)
		setSuccess(null)

		try {
			await backendAPI.delete('/api/user/delete-own-account/')

			localStorage.removeItem('userInfo')
			localStorage.removeItem('authToken')
			localStorage.removeItem('mockAuthToken')
			localStorage.removeItem('merchant_id')
			localStorage.removeItem('access_token')
			localStorage.removeItem('userId')
			window.location.href = '/sign-up/'
		} catch (error) {
			console.error('Error deleting account:', error)
			console.error('Response status:', error.response?.status)
			console.error('Response data:', error.response?.data)
			setError(error.response?.data?.detail || error?.message || t('profile.failedToDelete'))
		} finally {
			setDeleteLoading(false)
		}
	}

	const pageHeader = (
		<div className="klerk-header d-flex justify-content-between align-items-center mb-4">
			<h4 className="page-title m-0">{t('profile.title')}</h4>
			<div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
				<div className="user-profile-widget d-flex align-items-center">
					<NavLink to="/merchant-profile/" className="store-name">
						{businessData.business_name || '…'}{' '}
						<span className="wide-v">v</span>
					</NavLink>
					<span className="status-badge">{t('profile.active')}</span>
					<ProfileDropdown />
				</div>
				<button
					type="button"
					className="klerk-btn btn-secondary klerk-header-logout"
					onClick={logoutHandler}
				>
					{t('profile.logOut')}
				</button>
			</div>
		</div>
	)

	const sidebar = (
		<div className="klerk-sidebar">
			<div className="klerk-logo">Klerk</div>
			<MerchantSidebarMenu dashboardEnd />
		</div>
	)

	if (loading) {
		return (
			<div className="klerk-app-container">
				{sidebar}
				<div className="klerk-main-content">
					{pageHeader}
					<div className="text-center mt-5">
						<p>{t('profile.loadingProfile')}</p>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="klerk-app-container">
			{sidebar}
			<div className="klerk-main-content">
				{pageHeader}

				<div style={{ width: '60%', alignSelf: 'center' }}>
							{/* Alerts */}
							{error && (
								<Alert variant="danger" onClose={() => setError(null)} dismissible>
									{error}
								</Alert>
							)}
							{success && (
								<Alert variant="success" onClose={() => setSuccess(null)} dismissible>
									{success}
								</Alert>
							)}

							{activeTab === 'profile' && !isEditMode && (
								<div className="d-flex justify-content-end mb-3">
									<Button variant="primary" onClick={() => setIsEditMode(true)}>
										{t('profile.editProfile')}
									</Button>
								</div>
							)}

							{/* Tabs */}
							<Tabs
								activeKey={activeTab}
								onSelect={(key) => {
									setActiveTab(key || 'profile')
									setIsEditMode(false)
								}}
								className="mb-3"
							>
								{/* Business Information Tab */}
								<Tab eventKey="profile" title={t('profile.businessInformation')}>
									{/* Business Information Section */}
									<Card className="mb-4">										
										<Card.Body>
											<Form>
												{/* Account Email (Read-only) */}
												<Form.Group className="mb-3">
													<Form.Label>{t('profile.accountEmail')}</Form.Label>
													<div className="form-control-plaintext">
														<strong>{userInfo?.email || t('profile.notProvided')}</strong>
													</div>
													<small className="text-muted">{t('profile.emailLoginNote')}</small>
												</Form.Group>

												{/* Business Name */}
												<Form.Group className="mb-3">
													<Form.Label>{t('profile.businessName')} *</Form.Label>
													{isEditMode ? (
														<Form.Control
															type="text"
															name="business_name"
															value={businessData.business_name}
															onChange={handleBusinessChange}
															placeholder={t('profile.enterBusinessName')}
															required
														/>
													) : (
														<div className="form-control-plaintext">
															<strong>{businessData.business_name || t('profile.notProvided')}</strong>
														</div>
													)}
												</Form.Group>

												{/* Address */}
												<Form.Group className="mb-3">
													<Form.Label>{t('profile.address')} *</Form.Label>
													{isEditMode ? (
														<Form.Control
															type="text"
															name="address"
															value={businessData.address}
															onChange={handleBusinessChange}
															placeholder={t('profile.enterAddress')}
															required
														/>
													) : (
														<div className="form-control-plaintext">
															{businessData.address || t('profile.notProvided')}
														</div>
													)}
												</Form.Group>

												{/* Category */}
												<Form.Group className="mb-3">
													<Form.Label>{t('profile.category')} *</Form.Label>
													{isEditMode ? (
														<Form.Select
															name="category"
															value={businessData.category}
															onChange={handleBusinessChange}
															required
														>
															<option value="">{t('profile.selectCategory')}</option>
															{categoryOptions.map((option) => (
																<option key={option.value} value={option.value}>
																	{option.label}
																</option>
															))}
														</Form.Select>
													) : (
														<div className="form-control-plaintext">
															{businessData.category || t('profile.notProvided')}
														</div>
													)}
												</Form.Group>

												{/* Phone */}
												<Form.Group className="mb-3">
													<Form.Label>{t('profile.phone')} *</Form.Label>
													{isEditMode ? (
														<Form.Control
															type="tel"
															name="phone"
															value={businessData.phone}
															onChange={handleBusinessChange}
															placeholder={t('profile.enterPhone')}
															required
														/>
													) : (
														<div className="form-control-plaintext">
															{businessData.phone || t('profile.notProvided')}
														</div>

													)}
												</Form.Group>

												{/* Google Place ID */}
												<Form.Group className="mb-3">
													<Form.Label>{t('profile.googlePlaceId')}</Form.Label>
													{isEditMode ? (
														<>
															<Form.Control
																type="text"
																name="google_place_id"
																value={businessData.google_place_id}
																onChange={handleBusinessChange}
																placeholder={t('profile.googlePlaceIdPlaceholder')}
															/>
															<Form.Text className="text-muted">{t('profile.googlePlaceIdHint')}</Form.Text>
														</>
													) : (
														<div className="form-control-plaintext">
															{businessData.google_place_id || t('profile.notProvided')}
														</div>
													)}
												</Form.Group>

												{String(businessData.google_place_id || '').trim() && (
													<Form.Group className="mb-3">
														{isEditMode ? (
															<Form.Check
																type="checkbox"
																id="free_visit_for_review"
																name="free_visit_for_review"
																label={t('profile.freeVisitForReview')}
																checked={Boolean(businessData.free_visit_for_review)}
																onChange={handleBusinessChange}
															/>
														) : (
															<>
																<Form.Label>{t('profile.freeVisitForReview')}</Form.Label>
																<div className="form-control-plaintext">
																	{businessData.free_visit_for_review ? t('profile.yes') : t('profile.no')}
																</div>
															</>
														)}
													</Form.Group>
												)}

												<Form.Group className="mb-3">
													<Form.Label>{"MerchantID"} *</Form.Label>
													<div className="form-control-plaintext">
														{merchanID}
													</div>
												</Form.Group>
											</Form>
										</Card.Body>
									</Card>

									{/* Action Buttons */}
									{isEditMode && (
										<div className="action-buttons mb-4">
											<Button
												variant="success"
												onClick={handleSave}
												className="me-2"
												disabled={saveLoading}
											>
												{saveLoading ? t('profile.saving') : t('profile.saveChanges')}
											</Button>
											<Button
												variant="secondary"
												onClick={handleCancel}
											>
												{t('profile.cancel')}
											</Button>
										</div>
									)}
								</Tab>

								{/* Language Preference Tab */}
								<Tab eventKey="language" title={t('profile.languagePreference')}>
									<p className="text-uppercase text-muted small fw-semibold mb-2">{t('profile.language')}</p>
									<Card className="mb-4">
										<Card.Body className="d-flex justify-content-between align-items-center">
											<div>
												<div className="fw-bold">{t('profile.displayLanguage')}</div>
												<small className="text-muted">{t('profile.chooseLanguage')}</small>
											</div>
											<DropdownComponent
												title="nav.language"
												options={languageOptions}
												selected={selectedLang}
												onSelect={handleLanguageChange}
												icon={<span className="klerk-dropdown-headerIcon">🌐</span>}
												position="bottom"
											/>
										</Card.Body>
									</Card>
								</Tab>

								{/* Cookie Settings Tab */}
								<Tab eventKey="cookie-settings" title={t('profile.cookieSettings')}>
									<p className="text-uppercase text-muted small fw-semibold mb-2">{t('profile.cookieSettings')}</p>
									<Card className="mb-4">
										<Card.Body className="d-flex justify-content-between align-items-center">
											<div>
												<div className="fw-bold">{t('profile.manageCookies')}</div>
												
											</div>
											<Button
												variant="secondary"
												onClick={() => window.Cookiebot?.renew()}
											>
												<i className="uil uil-setting me-1"></i>
												{t('navbar.cookieSettings')}
											</Button>
										</Card.Body>
									</Card>
								</Tab>

								{/* Delete Account Tab */}
								<Tab eventKey="delete-account" title={t('profile.deleteAccount')}>
									<Card className="mb-4 border-danger">
										<Card.Header className="bg-danger text-white">
											<h5 className="mb-0">{t('profile.deleteAccount')}</h5>
										</Card.Header>
										<Card.Body>
											<p>
												{t('profile.deleteAccountQuestion')}
											</p>

											<Button
												variant="danger"
												onClick={handleDeleteAccount}
												disabled={deleteLoading}
											>
												{deleteLoading ? t('profile.deleting') : t('profile.deleteAccount')}
											</Button>

										</Card.Body>
									</Card>
								</Tab>
							</Tabs>
				</div>
			</div>
		</div>
	)
}

export default MerchantProfile
